import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    MenuItem,
    Pagination,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import {
    Close,
    DownloadOutlined,
    EditOutlined,
    GridViewOutlined
} from "@mui/icons-material";
import type {
    SddProgressReport,
    SddProgressReportFilters,
    SddProgressReportPage as SddProgressReportPageData,
    SddProgressStatus,
    SddProgressSummary,
    SddScopeProgress
} from "@/api/api";
import { SDD_STAGES, type SddStageKey } from "./sddWorkflow";
import { FeatureStudioHierarchyCard } from "./FeatureStudioHierarchyCard";

const LAPTOP_FIT_QUERY = "@media (min-width: 1280px) and (min-height: 768px)";
const STANDARD_LAPTOP_HEIGHT_QUERY = "@media (min-width: 1280px) and (min-height: 768px) and (max-height: 900px)";

function entityId(entity: any): string {
    return String(entity?.featureid || entity?.user_story_id || entity?.id || entity?.story_id || "");
}

function entityName(entity: any): string {
    return entity?.feature_name || entity?.title || entity?.name || "Untitled";
}

function humanizeFormFactor(value: string): string {
    const normalized = value.trim().toLowerCase();
    if (normalized === "api") return "API";
    return normalized
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
        .join(" ");
}

function humanizeDescription(value: string): string {
    return value.replace(/\[\s*form_factor\s*:\s*([^\]]+)\]/gi, (_match, formFactor: string) =>
        `Primary form factor: ${humanizeFormFactor(formFactor)}`
    );
}

const STATUS_LABELS: Record<SddProgressStatus, string> = {
    accepted: "Accepted",
    in_progress: "In Progress",
    to_do: "To Do"
};

const STATUS_COLORS: Record<SddProgressStatus, { color: string; backgroundColor: string }> = {
    accepted: { color: "#008000", backgroundColor: "rgba(0,128,0,.10)" },
    in_progress: { color: "#103699", backgroundColor: "#EDF2FF" },
    to_do: { color: "#64748b", backgroundColor: "#f1f5f9" }
};

const STAGE_COLORS: Record<SddStageKey, string> = {
    specify: "#103699",
    plan: "#1954ED",
    task_breakdown: "#008000",
    implement: "#94B2FF",
    validate: "#A3BBF8"
};

export function ProductCubeIcon({ size = 22, iconSize = 12 }: { size?: number; iconSize?: number }) {
    return (
        <Box
            component="span"
            aria-hidden="true"
            sx={{
                width: size,
                height: size,
                flex: `0 0 ${size}px`,
                borderRadius: "6px",
                bgcolor: "#103699",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            <svg
                width={iconSize}
                height={iconSize}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
        </Box>
    );
}

export function FeatureLayersIcon({ size = 18, iconSize = 10 }: { size?: number; iconSize?: number }) {
    return (
        <Box
            component="span"
            aria-hidden="true"
            sx={{
                width: size,
                height: size,
                flex: `0 0 ${size}px`,
                borderRadius: "5px",
                bgcolor: "#94B2FF",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            <svg
                width={iconSize}
                height={iconSize}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polygon points="12 2 2 7 12 12 22 7 12 2" />
                <polyline points="2 17 12 22 22 17" />
                <polyline points="2 12 12 17 22 12" />
            </svg>
        </Box>
    );
}

export function StoryGridIcon({ size = 16, iconSize = 9 }: { size?: number; iconSize?: number }) {
    return (
        <Box
            component="span"
            aria-hidden="true"
            sx={{
                width: size,
                height: size,
                flex: `0 0 ${size}px`,
                borderRadius: "4px",
                bgcolor: "#334155",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            <svg
                width={iconSize}
                height={iconSize}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
        </Box>
    );
}

export function ProgressDonut({
    percent,
    size = 64,
    color = "#103699",
    label,
    fontSize
}: {
    percent: number;
    size?: number;
    color?: string;
    label?: string;
    fontSize?: number;
}) {
    return (
        <Box
            role="img"
            aria-label={label || `${percent}% of required work products accepted`}
            sx={{
                width: size,
                height: size,
                flex: `0 0 ${size}px`,
                borderRadius: "50%",
                background: `conic-gradient(${color} ${percent * 3.6}deg, #e2e8f0 0)`,
                display: "grid",
                placeItems: "center"
            }}
        >
            <Box
                sx={{
                    width: size - Math.max(7, size * 0.2),
                    height: size - Math.max(7, size * 0.2),
                    borderRadius: "50%",
                    bgcolor: "background.paper",
                    display: "grid",
                    placeItems: "center"
                }}
            >
                <Typography sx={{ fontSize: fontSize ?? (size > 50 ? 13 : size > 34 ? 9 : 7.5), fontWeight: 800, color: "#0f172a" }}>{percent}%</Typography>
            </Box>
        </Box>
    );
}

export function progressForScope(progress: SddProgressReport | null | undefined, feature?: any, story?: any): SddScopeProgress | null {
    if (!progress?.scope_progress) return null;
    const featureId = feature ? entityId(feature) : "";
    const storyId = story ? entityId(story) : "";
    if (storyId) {
        const owningFeature = progress.scope_progress.features.find(item => String(item.feature_id || "") === featureId);
        return owningFeature?.stories.find(item => String(item.story_id || "") === storyId) ||
            progress.scope_progress.features.flatMap(item => item.stories).find(item => String(item.story_id || "") === storyId) ||
            null;
    }
    if (featureId) {
        return progress.scope_progress.features.find(item => String(item.feature_id || "") === featureId) || null;
    }
    return progress.scope_progress.product;
}

export function ScopeProgressRings({
    progress,
    feature,
    story,
    scopeProgress,
    size,
    compact = false
}: {
    progress?: SddProgressReport | null;
    feature?: any;
    story?: any;
    scopeProgress?: SddProgressSummary | null;
    size?: number;
    compact?: boolean;
}) {
    const resolvedProgress = scopeProgress === undefined ? progressForScope(progress, feature, story) : scopeProgress;
    const ringSize = size ?? (compact ? 30 : 38);
    if (!resolvedProgress) {
        return <Typography sx={{ py: 0.75, fontSize: compact ? 9 : 10.5, color: "text.secondary" }}>Progress unavailable</Typography>;
    }
    return (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5,minmax(0,1fr))", gap: compact ? 0.5 : 0.75 }}>
            {resolvedProgress.stages.map(item => {
                const label = item.key === "task_breakdown" ? "Tasks" : item.key === "implement" ? "Impl" : item.key === "validate" ? "Val" : item.title;
                return (
                <Box key={item.key} sx={{ display: "grid", justifyItems: "center", gap: 0.35, minWidth: 0 }}>
                    <ProgressDonut
                        percent={item.percent}
                        size={ringSize}
                        color={STAGE_COLORS[item.key]}
                        label={`${label}: ${item.accepted} of ${item.total} accepted`}
                    />
                    <Typography noWrap sx={{ width: "100%", textAlign: "center", fontSize: ringSize >= 38 ? 9 : ringSize >= 32 ? 8.5 : 8, color: "text.secondary" }}>
                        {label}
                    </Typography>
                </Box>
                );
            })}
        </Box>
    );
}

export type ReportLoader = (filters: SddProgressReportFilters, page: number, pageSize: number) => Promise<SddProgressReportPageData>;
export type ReportExporter = (filters: SddProgressReportFilters) => Promise<{ blob: Blob; filename: string }>;

const HIERARCHY_FILTERS = [
    ["All", undefined],
    ["Product", "PRODUCT"],
    ["Feature", "FEATURE"],
    ["Story", "USERSTORY"]
] as const;
const STEP_FILTERS = [
    ["All", undefined],
    ["Specify", "specify"],
    ["Plan", "plan"],
    ["Define Tasks", "task_breakdown"],
    ["Implement", "implement"],
    ["Validate", "validate"]
] as const;
const STATUS_FILTERS = [
    ["All", undefined],
    ["To Do", "to_do"],
    ["In Progress", "in_progress"],
    ["Accepted", "accepted"]
] as const;

function FilterGroup<T extends string>({
    label,
    options,
    value,
    onChange
}: {
    label: string;
    options: readonly (readonly [string, T | undefined])[];
    value?: T;
    onChange: (value?: T) => void;
}) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
            <Typography sx={{ mr: 0.2, fontSize: 9.5, fontWeight: 800, letterSpacing: ".09em", color: "#64748b" }}>{label}</Typography>
            <Box sx={{ display: "flex", gap: 0.25, p: 0.4, borderRadius: "9px", bgcolor: "#f1f5f9" }}>
                {options.map(([optionLabel, optionValue]) => {
                    const selected = value === optionValue;
                    return (
                        <Button
                            key={optionLabel}
                            size="small"
                            aria-pressed={selected}
                            onClick={() => onChange(optionValue)}
                            sx={{
                                minWidth: 0,
                                height: 29,
                                px: 1.2,
                                borderRadius: "7px",
                                bgcolor: selected ? "#fff" : "transparent",
                                color: "#475569",
                                boxShadow: selected ? "0 1px 3px rgba(15,23,42,.12)" : "none",
                                fontSize: 11,
                                fontWeight: selected ? 700 : 500,
                                textTransform: "none",
                                "&:hover": { bgcolor: selected ? "#fff" : "rgba(255,255,255,.55)" }
                            }}
                        >
                            {optionLabel}
                        </Button>
                    );
                })}
            </Box>
        </Box>
    );
}

export function SddProgressReportPage({
    onClose,
    loadPage,
    exportXlsx
}: {
    onClose: () => void;
    loadPage: ReportLoader;
    exportXlsx: ReportExporter;
}) {
    const [filters, setFilters] = useState<SddProgressReportFilters>({ stage: "specify" });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [report, setReport] = useState<SddProgressReportPageData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [exporting, setExporting] = useState(false);
    useEffect(() => {
        let active = true;
        setLoading(true);
        setError("");
        void loadPage(filters, page, pageSize)
            .then(result => {
                if (active) setReport(result);
            })
            .catch(() => {
                if (active) setError("The SDD progress report could not be loaded.");
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [filters, loadPage, page, pageSize]);

    const updateFilter = <K extends keyof SddProgressReportFilters>(key: K, value: SddProgressReportFilters[K]) => {
        setPage(1);
        setFilters(current => ({ ...current, [key]: value }));
    };
    const handleExport = async () => {
        setExporting(true);
        setError("");
        try {
            const { blob, filename } = await exportXlsx(filters);
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = filename;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch {
            setError("The XLSX report could not be exported.");
        } finally {
            setExporting(false);
        }
    };
    const counts = report?.status_counts;

    return (
        <Box role="region" aria-label="SDD Work Product Progress Report" sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
            <Box sx={{ minHeight: 84, px: 3.25, py: 1.75, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography component="h1" sx={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
                        SDD Work Product Progress Report
                    </Typography>
                    <Typography noWrap sx={{ mt: 0.4, fontSize: 12, color: "#64748b" }}>
                        {report?.product_name || "Selected product"} — one row per work product, by hierarchy level and SDD step.
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pt: 0.15 }}>
                    {(["accepted", "in_progress", "to_do"] as const).map(value => (
                        <Box key={value} sx={{ display: "flex", alignItems: "center", gap: 0.65 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: value === "accepted" ? "#008000" : value === "in_progress" ? "#103699" : "#e2e8f0" }} />
                            <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                {STATUS_LABELS[value]} {counts?.[value] ?? 0}
                            </Typography>
                        </Box>
                    ))}
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={exporting || !report}
                        startIcon={exporting ? <CircularProgress size={12} /> : <DownloadOutlined sx={{ fontSize: "15px !important" }} />}
                        onClick={() => void handleExport()}
                        sx={{ height: 31, borderColor: "#e2e8f0", borderRadius: "9px", fontSize: 11, textTransform: "none" }}
                    >
                        Export XLSX
                    </Button>
                    <IconButton aria-label="Close progress report" onClick={onClose} sx={{ width: 34, height: 34, border: "1px solid #e5e7eb", borderRadius: "9px" }}>
                        <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{ px: 3.25, py: 1.25, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 1.8, flexWrap: "wrap" }}>
                <FilterGroup label="HIERARCHY LEVEL" options={HIERARCHY_FILTERS} value={filters.hierarchy_level} onChange={value => updateFilter("hierarchy_level", value)} />
                <FilterGroup label="SDD STEP" options={STEP_FILTERS} value={filters.stage} onChange={value => updateFilter("stage", value)} />
                <FilterGroup label="STATUS" options={STATUS_FILTERS} value={filters.status} onChange={value => updateFilter("status", value)} />
                <Typography sx={{ ml: "auto", fontSize: 10.5, color: "#64748b" }}>
                    {report ? `${report.pagination.filtered_rows} of ${report.pagination.overall_rows} rows` : "Loading rows…"}
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mx: 3.25, mt: 1.5 }}>{error}</Alert>}
            <TableContainer sx={{ flex: 1, minHeight: 0, position: "relative" }}>
                {loading && (
                    <Box sx={{ position: "absolute", inset: 0, zIndex: 3, display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,.7)" }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                <Table stickyHeader size="small" aria-label="SDD work product progress">
                    <TableHead>
                        <TableRow>
                            {["Hierarchy Level", "Product Name", "Feature Name", "Story Name", "SDD Step", "Work Product", "Status"].map(label => (
                                <TableCell key={label} sx={{ py: 1.1, bgcolor: "#fff", borderBottom: "1px solid #e5e7eb", fontSize: 9.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: "#64748b" }}>
                                    {label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {report?.rows.map(row => (
                            <TableRow key={`${row.level}-${row.feature_id}-${row.story_id}-${row.canonical_variable}`} hover>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={row.hierarchy_label}
                                        sx={{
                                            height: 23,
                                            bgcolor: row.level === "PRODUCT" ? "#DBE5FF" : row.level === "FEATURE" ? "#EDF2FF" : "#f1f5f9",
                                            color: row.level === "PRODUCT" ? "#103699" : row.level === "FEATURE" ? "#1954ED" : "#475569",
                                            fontSize: 9.5,
                                            fontWeight: 700,
                                            "&:hover": {
                                                bgcolor: row.level === "PRODUCT" ? "#DBE5FF" : row.level === "FEATURE" ? "#EDF2FF" : "#f1f5f9"
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 180 }}><Typography noWrap title={row.product_name} sx={{ fontSize: 11.5 }}>{row.product_name}</Typography></TableCell>
                                <TableCell sx={{ maxWidth: 200 }}><Typography noWrap title={row.feature_name || "N/A"} sx={{ fontSize: 11.5, color: row.feature_name ? "#334155" : "#64748b" }}>{row.feature_name || "N/A"}</Typography></TableCell>
                                <TableCell sx={{ maxWidth: 220 }}><Typography noWrap title={row.story_display_name || "N/A"} sx={{ fontSize: 11.5, color: row.story_display_name ? "#334155" : "#64748b" }}>{row.story_display_name || "N/A"}</Typography></TableCell>
                                <TableCell sx={{ fontSize: 11.5, color: "#103699" }}>
                                    {row.stage === "task_breakdown" ? "Define Tasks" : row.stage_label}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 360 }}><Typography noWrap title={row.work_product_label} sx={{ fontSize: 11.5 }}>{row.work_product_label}</Typography></TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={row.status_label.toUpperCase()}
                                        sx={{
                                            height: 23,
                                            ...STATUS_COLORS[row.status],
                                            fontSize: 9.5,
                                            fontWeight: 800,
                                            "&:hover": { backgroundColor: STATUS_COLORS[row.status].backgroundColor }
                                        }}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && report?.rows.length === 0 && (
                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 8, color: "#64748b" }}>No work products match these filters.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ minHeight: 54, px: 3.25, borderTop: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Typography sx={{ fontSize: 10.5, color: "#64748b" }}>
                        {report?.pagination.from_row || 0}–{report?.pagination.to_row || 0} of {report?.pagination.filtered_rows || 0} filtered rows
                    </Typography>
                    <Typography component="label" htmlFor="sdd-report-page-size" sx={{ fontSize: 10.5, color: "#64748b" }}>
                        Rows per page
                    </Typography>
                    <Select
                        id="sdd-report-page-size"
                        size="small"
                        value={pageSize}
                        inputProps={{ "aria-label": "Rows per page" }}
                        onChange={event => {
                            setPageSize(Number(event.target.value));
                            setPage(1);
                        }}
                        sx={{ height: 30, minWidth: 70, fontSize: 11 }}
                    >
                        {[25, 50, 100].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                    </Select>
                </Box>
                <Pagination count={report?.pagination.total_pages || 1} page={page} onChange={(_, value) => setPage(value)} size="small" color="primary" disabled={loading} />
            </Box>
        </Box>
    );
}

export function ProductHierarchyPage({
    onClose,
    project,
    progress,
    onAddFeature,
    onAddStory,
    onEditFeature,
    onEditStory,
    onFeatureSelect,
    onStorySelect
}: {
    onClose: () => void;
    project: any;
    progress?: SddProgressReport | null;
    onAddFeature?: () => void;
    onAddStory?: (feature: any) => void;
    onEditFeature?: (feature: any) => void;
    onEditStory?: (feature: any, story: any) => void;
    onFeatureSelect?: (feature: any) => void;
    onStorySelect?: (feature: any, story: any) => void;
}) {
    const features = Array.isArray(project?.features) ? project.features : [];
    const descriptionFor = (entity: any, fallback: string) =>
        humanizeDescription(entity?.description || entity?.project_description || entity?.feature_description || entity?.story_description || fallback);
    const statusFor = (feature: any, story?: any) => {
        const featureId = entityId(feature);
        const storyId = story ? entityId(story) : "";
        const rows = (progress?.rows || []).filter(row => {
            if (storyId) return String(row.story_id || "") === storyId;
            return String(row.feature_id || "") === featureId;
        });
        const rawStatus = String(story?.status || feature?.status || story?.state || feature?.state || "")
            .trim()
            .toLowerCase();
        if ((rows.length > 0 && rows.every(row => row.status === "accepted")) || ["complete", "completed", "done", "accepted"].includes(rawStatus)) {
            return { label: "Complete", color: "#008000", background: "rgba(0,128,0,.10)" };
        }
        if (
            rows.some(row => row.status === "accepted" || row.status === "in_progress") ||
            ["in progress", "in_progress", "active", "started"].includes(rawStatus)
        ) {
            return { label: "In Progress", color: "#1954ED", background: "#EDF2FF" };
        }
        return { label: "To Do", color: "#6b7280", background: "#f1f5f9" };
    };

    return (
        <Box
            component="section"
            aria-labelledby="product-hierarchy-title"
            sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#fff" }}
        >
            <Box
                sx={{
                    flex: "0 0 auto",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1.5,
                    px: "28px",
                    py: "18px",
                    borderBottom: "1px solid rgba(0,0,0,.07)"
                }}
            >
                <Box>
                    <Typography id="product-hierarchy-title" component="h2" sx={{ m: 0, fontSize: 17, lineHeight: 1.35, fontWeight: 600, color: "#0f172a" }}>
                        Product / Feature / Story
                    </Typography>
                    <Typography sx={{ mt: "2px", fontSize: 12.5, color: "#6b7280" }}>Full hierarchy for this product</Typography>
                </Box>
                <IconButton
                    size="small"
                    aria-label="Back to Feature Studio"
                    onClick={onClose}
                    sx={{ width: 32, height: 32, border: "1px solid rgba(0,0,0,.07)", borderRadius: "8px", color: "#4b5563" }}
                >
                    <Close sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: "28px", py: "24px" }}>
                <Box sx={{ width: "100%", maxWidth: 820 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", pb: "14px", mb: "14px", borderBottom: "1px solid rgba(0,0,0,.07)" }}>
                        <ProductCubeIcon size={24} iconSize={13} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14, lineHeight: 1.4, fontWeight: 600, color: "#0f172a" }}>
                                {project?.project_name || project?.name || "Selected product"}
                            </Typography>
                            <Typography sx={{ mt: "2px", fontSize: 12.5, lineHeight: 1.5, color: "#4b5563" }}>
                                {descriptionFor(project, "No product description available.")}
                            </Typography>
                        </Box>
                    </Box>

                    {onAddFeature && (
                        <Button
                            size="small"
                            onClick={onAddFeature}
                            sx={{
                                mb: "14px",
                                height: 30,
                                px: "12px",
                                border: "1px dashed rgba(0,0,0,.10)",
                                borderRadius: "7px",
                                color: "#103699",
                                fontSize: 12,
                                fontWeight: 600,
                                textTransform: "none"
                            }}
                        >
                            + Add Feature
                        </Button>
                    )}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                        {features.map((feature: any, featureIndex: number) => {
                            const stories = feature.user_stories || feature.userStories || feature.stories || [];
                            const featureStatus = statusFor(feature);
                            return (
                                <Box key={entityId(feature) || featureIndex} sx={{ pl: "12px" }}>
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "9px" }}>
                                        <Box sx={{ mt: "1px", display: "flex" }}>
                                            <FeatureLayersIcon size={20} iconSize={11} />
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontSize: 13, lineHeight: 1.45, fontWeight: 600, color: "#0f172a" }}>
                                                {entityName(feature)}
                                            </Typography>
                                            <Typography sx={{ mt: "1px", fontSize: 12, lineHeight: 1.5, color: "#4b5563" }}>
                                                {descriptionFor(feature, "No feature description available.")}
                                            </Typography>
                                        </Box>
                                        <Box
                                            component="span"
                                            sx={{
                                                flex: "0 0 auto",
                                                px: "8px",
                                                py: "3px",
                                                borderRadius: "999px",
                                                bgcolor: featureStatus.background,
                                                color: featureStatus.color,
                                                fontSize: 9.5,
                                                lineHeight: 1.4,
                                                fontWeight: 700,
                                                letterSpacing: ".04em",
                                                textTransform: "uppercase"
                                            }}
                                        >
                                            {featureStatus.label}
                                        </Box>
                                        {onEditFeature && (
                                            <Tooltip title="Edit feature">
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Edit ${entityName(feature)}`}
                                                    onClick={() => onEditFeature(feature)}
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        flex: "0 0 auto",
                                                        border: "1px solid rgba(0,0,0,.07)",
                                                        borderRadius: "6px",
                                                        color: "#4b5563"
                                                    }}
                                                >
                                                    <EditOutlined sx={{ fontSize: 12 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: "7px", mt: "8px", pl: "29px" }}>
                                        {stories.map((story: any, storyIndex: number) => {
                                            const storyStatus = statusFor(feature, story);
                                            return (
                                                <Box key={entityId(story) || storyIndex} sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                                    <Box sx={{ mt: "1px", display: "flex" }}>
                                                        <StoryGridIcon />
                                                    </Box>
                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                        <Typography sx={{ fontSize: 12.5, lineHeight: 1.45, fontWeight: 600, color: "#0f172a" }}>
                                                            {entityName(story)}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11.5, lineHeight: 1.45, color: "#6b7280" }}>
                                                            {descriptionFor(story, `Implementation task supporting ${entityName(feature).toLowerCase()}.`)}
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            flex: "0 0 auto",
                                                            px: "7px",
                                                            py: "3px",
                                                            borderRadius: "999px",
                                                            bgcolor: storyStatus.background,
                                                            color: storyStatus.color,
                                                            fontSize: 9,
                                                            lineHeight: 1.4,
                                                            fontWeight: 700,
                                                            letterSpacing: ".04em",
                                                            textTransform: "uppercase"
                                                        }}
                                                    >
                                                        {storyStatus.label}
                                                    </Box>
                                                    {onEditStory && (
                                                        <Tooltip title="Edit story">
                                                            <IconButton
                                                                size="small"
                                                                aria-label={`Edit ${entityName(story)}`}
                                                                onClick={() => onEditStory(feature, story)}
                                                                sx={{
                                                                    width: 22,
                                                                    height: 22,
                                                                    flex: "0 0 auto",
                                                                    border: "1px solid rgba(0,0,0,.07)",
                                                                    borderRadius: "6px",
                                                                    color: "#4b5563"
                                                                }}
                                                            >
                                                                <EditOutlined sx={{ fontSize: 11 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                        {onAddStory && (
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    onFeatureSelect?.(feature);
                                                    onAddStory(feature);
                                                }}
                                                sx={{
                                                    alignSelf: "flex-start",
                                                    mt: "2px",
                                                    height: 24,
                                                    px: "9px",
                                                    border: "1px dashed rgba(0,0,0,.10)",
                                                    borderRadius: "6px",
                                                    color: "#103699",
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    textTransform: "none"
                                                }}
                                            >
                                                + Add Story
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export function FeatureStorySelectionPage({
    onClose,
    project,
    progress,
    selectedFeature,
    selectedStory,
    onFeatureSelect,
    onStorySelect
}: {
    onClose: () => void;
    project: any;
    progress?: SddProgressReport | null;
    selectedFeature?: any;
    selectedStory?: any;
    onFeatureSelect?: (feature: any) => void;
    onStorySelect?: (feature: any, story: any) => void;
}) {
    const features = Array.isArray(project?.features) ? project.features : [];
    const [previewFeatureId, setPreviewFeatureId] = useState(() => entityId(selectedFeature));
    const [previewStoryId, setPreviewStoryId] = useState(() => entityId(selectedStory));
    const [selectionPending, setSelectionPending] = useState(false);
    const selectedRowRef = useRef<HTMLDivElement | null>(null);
    const returnTimerRef = useRef<number | null>(null);
    const descriptionFor = (entity: any, fallback: string) =>
        humanizeDescription(entity?.description || entity?.project_description || entity?.feature_description || entity?.story_description || fallback);

    useEffect(() => {
        const frame = window.requestAnimationFrame(() => selectedRowRef.current?.scrollIntoView?.({ block: "center" }));
        return () => {
            window.cancelAnimationFrame(frame);
            if (returnTimerRef.current !== null) window.clearTimeout(returnTimerRef.current);
        };
    }, []);

    // Chat selections are resolved outside this page and arrive as updated
    // props. Keep the visible selector state aligned with those canonical
    // selections instead of retaining the values captured on first mount.
    useEffect(() => {
        setPreviewFeatureId(entityId(selectedFeature));
        setPreviewStoryId(entityId(selectedStory));
        setSelectionPending(false);
    }, [selectedFeature, selectedStory]);

    const chooseFeature = (feature: any) => {
        if (selectionPending) return;
        if (entityId(feature) === previewFeatureId) {
            setPreviewFeatureId("");
            setPreviewStoryId("");
            onFeatureSelect?.(null);
            return;
        }
        setPreviewFeatureId(entityId(feature));
        setPreviewStoryId("");
        onFeatureSelect?.(feature);
    };
    const chooseStory = (feature: any, story: any) => {
        if (selectionPending) return;
        setPreviewFeatureId(entityId(feature));
        setPreviewStoryId(entityId(story));
        setSelectionPending(true);
        returnTimerRef.current = window.setTimeout(() => {
            onStorySelect?.(feature, story);
            onClose();
        }, 450);
    };

    return (
        <Box role="region" aria-label="Select Feature and Story" sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
            <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "flex-start", gap: 2, borderBottom: "1px solid #e5e7eb" }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography component="h1" sx={{ m: 0, fontSize: 18, lineHeight: 1.35, fontWeight: 700, color: "#111827" }}>
                        Select Feature &amp; Story
                    </Typography>
                    <Typography sx={{ mt: 0.75, fontSize: 12.5, color: "#6b7280" }}>
                        Choose one feature and one story to build with the Specification Driven Development workflow.
                    </Typography>
                </Box>
                <IconButton aria-label="Close feature and story selection" onClick={onClose} sx={{ width: 34, height: 34, border: "1px solid #e5e7eb", borderRadius: "9px" }}>
                    <Close sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <Box className="fs-scroll" sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2.5, py: 2.25 }}>
                <Box sx={{ width: "min(100%, 790px)" }}>
                    <Box sx={{ pb: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 220px" }, alignItems: "center", gap: 2, borderBottom: "1px solid #e5e7eb" }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
                            <ProductCubeIcon size={24} iconSize={13} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                                    {project?.project_name || project?.name || "Selected product"}
                                </Typography>
                                <Typography sx={{ mt: 0.25, fontSize: 12.5, lineHeight: 1.5, color: "#4b5563" }}>
                                    {descriptionFor(project, "No product description available.")}
                                </Typography>
                            </Box>
                        </Box>
                        <ScopeProgressRings progress={progress} size={28} compact />
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, pt: 1.5 }}>
                        {features.map((feature: any) => {
                            const stories = feature.user_stories || feature.userStories || feature.stories || [];
                            const featureSelected = entityId(feature) === previewFeatureId;
                            return (
                                <Box
                                    key={entityId(feature)}
                                    data-testid={`feature-selection-${entityId(feature)}`}
                                    data-selected={featureSelected ? "true" : "false"}
                                    ref={featureSelected && !previewStoryId ? selectedRowRef : undefined}
                                    sx={{
                                        borderRadius: "11px",
                                        bgcolor: featureSelected ? "rgba(16, 54, 153,.065)" : "transparent"
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: "10px 12px",
                                            display: "grid",
                                            gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 102px 220px" },
                                            alignItems: "center",
                                            gap: 1.25
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
                                            <FeatureLayersIcon size={20} iconSize={11} />
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography noWrap sx={{ fontSize: 13, fontWeight: 650, color: "#111827" }}>
                                                    {entityName(feature)}
                                                </Typography>
                                                <Typography sx={{ mt: 0.2, fontSize: 11.5, lineHeight: 1.45, color: "#6b7280" }}>
                                                    {descriptionFor(feature, "No feature description available.")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Button
                                            data-ui-bridge={`sdd.choose-feature.${entityId(feature)}`}
                                            data-ui-label={`Choose feature ${entityName(feature)}`}
                                            data-ui-role="button"
                                            data-ui-group="sdd-scope-selection"
                                            data-ui-description={`Choose ${entityName(feature)} as the active SDD feature without leaving the selector`}
                                            size="small"
                                            variant="outlined"
                                            disabled={selectionPending}
                                            onClick={() => chooseFeature(feature)}
                                            sx={{
                                                width: featureSelected ? 75 : 114,
                                                minWidth: featureSelected ? 75 : 114,
                                                height: 28,
                                                px: "10px",
                                                borderRadius: "8px",
                                                borderColor: featureSelected ? "primary.main" : "#e5e7eb",
                                                color: featureSelected ? "primary.main" : "#475569",
                                                bgcolor: "#fff",
                                                fontSize: 11.5,
                                                lineHeight: 1,
                                                fontWeight: 600,
                                                textTransform: "none",
                                                "&:hover": featureSelected ? { borderColor: "primary.main" } : undefined,
                                                "&.Mui-disabled": {
                                                    borderColor: featureSelected ? "primary.main" : "#e5e7eb",
                                                    color: featureSelected ? "primary.main" : "#475569",
                                                    bgcolor: "#fff"
                                                }
                                            }}
                                        >
                                            {featureSelected ? "Selected" : "Choose Feature"}
                                        </Button>
                                        <ScopeProgressRings progress={progress} feature={feature} size={26} compact />
                                    </Box>

                                    {stories.map((story: any) => {
                                        const storySelected = entityId(story) === previewStoryId;
                                        return (
                                            <Box
                                                key={entityId(story)}
                                                data-testid={`story-selection-${entityId(story)}`}
                                                data-selected={storySelected ? "true" : "false"}
                                                ref={storySelected ? selectedRowRef : undefined}
                                                sx={{
                                                    ml: 5,
                                                    p: "8px 12px",
                                                    display: "grid",
                                                    gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 96px 190px" },
                                                    alignItems: "center",
                                                    gap: 1.25,
                                                    borderRadius: "9px",
                                                    bgcolor: storySelected ? "rgba(16, 54, 153,.075)" : "transparent"
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
                                                    <StoryGridIcon size={18} iconSize={10} />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: "#1f2937" }}>
                                                            {story.story_number ? `${story.story_number} · ` : ""}
                                                            {entityName(story)}
                                                        </Typography>
                                                        <Typography sx={{ mt: 0.15, fontSize: 11, lineHeight: 1.4, color: "#6b7280" }}>
                                                            {descriptionFor(story, `Implementation task supporting ${entityName(feature).toLowerCase()}.`)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Button
                                                    data-ui-bridge={`sdd.choose-story.${entityId(story)}`}
                                                    data-ui-label={`Choose story ${story.story_number ? `${story.story_number} · ` : ""}${entityName(story)}`}
                                                    data-ui-role="button"
                                                    data-ui-group="sdd-scope-selection"
                                                    data-ui-description={`Choose ${entityName(story)} as the active SDD user story and return to Feature Studio`}
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={selectionPending}
                                                    onClick={() => chooseStory(feature, story)}
                                                    sx={{
                                                        width: storySelected ? 74 : 96,
                                                        minWidth: storySelected ? 74 : 96,
                                                        height: 26,
                                                        px: "9px",
                                                        borderRadius: "8px",
                                                        borderColor: storySelected ? "primary.main" : "#e5e7eb",
                                                        color: storySelected ? "primary.main" : "#475569",
                                                        bgcolor: "#fff",
                                                        fontSize: 11,
                                                        lineHeight: 1,
                                                        fontWeight: 600,
                                                        textTransform: "none",
                                                        "&:hover": storySelected ? { borderColor: "primary.main" } : undefined,
                                                        "&.Mui-disabled": {
                                                            borderColor: storySelected ? "primary.main" : "#e5e7eb",
                                                            color: storySelected ? "primary.main" : "#475569",
                                                            bgcolor: "#fff"
                                                        }
                                                    }}
                                                >
                                                    {storySelected ? "Selected" : "Choose Story"}
                                                </Button>
                                                <ScopeProgressRings progress={progress} feature={feature} story={story} size={24} compact />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export function FeatureStudioWorkflowOverview({
    project,
    feature,
    story,
    progress,
    progressLoading,
    contextMesh,
    onOpenScopePicker,
    onOpenHierarchy,
    onOpenProgressReport
}: {
    project: any;
    feature?: any;
    story?: any;
    progress?: SddProgressReport | null;
    progressLoading?: boolean;
    contextMesh?: React.ReactNode;
    onOpenScopePicker?: () => void;
    onOpenHierarchy?: () => void;
    onOpenProgressReport?: () => void;
}) {
    const totals = progress?.totals || { total: 0, accepted: 0, in_progress: 0, to_do: 0, percent: 0 };
    const selectedFeatureName = feature ? entityName(feature) : "Select a feature";
    const selectedStoryName = story ? entityName(story) : "Select a story";
    const cardSx = { minWidth: 0, border: "1px solid rgba(15,23,42,.10)", borderRadius: "10px", bgcolor: "background.paper" } as const;
    const stepLabel = (number: number, text: string, showFullTextOnLaptop = false) => (
        <Box sx={{ minWidth: 0, display: "flex", alignItems: showFullTextOnLaptop ? "flex-start" : "center", gap: 1 }}>
            <Chip
                label={`Step ${number}`}
                size="small"
                sx={{
                    height: 24,
                    bgcolor: "#103699",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: ".04em",
                    "& .MuiChip-label": { px: "11px" },
                    [LAPTOP_FIT_QUERY]: {
                        height: 20,
                        fontSize: 9.5,
                        "& .MuiChip-label": { px: "8px" }
                    }
                }}
            />
            <Typography
                sx={{
                    minWidth: 0,
                    fontSize: 13,
                    color: "#475569",
                    [LAPTOP_FIT_QUERY]: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                    ...(showFullTextOnLaptop
                        ? {
                              [STANDARD_LAPTOP_HEIGHT_QUERY]: {
                                  overflow: "visible",
                                  textOverflow: "clip",
                                  whiteSpace: "normal",
                                  lineHeight: 1.25
                              }
                          }
                        : {})
                }}
            >
                {text}
            </Typography>
        </Box>
    );

    return (
        <Box data-testid="feature-studio-workflow-overview" sx={{ minWidth: 0, mb: 2, flexShrink: 0, [LAPTOP_FIT_QUERY]: { mb: 1 } }}>
            <Typography
                component="h1"
                sx={{
                    m: 0,
                    fontSize: 20,
                    lineHeight: 1.25,
                    fontWeight: 500,
                    letterSpacing: "-.01em",
                    color: "#0f172a",
                    [LAPTOP_FIT_QUERY]: { fontSize: 16, lineHeight: 1.2 }
                }}
            >
                Specification Driven Development - Build your product in 3 easy steps
            </Typography>
            <Box sx={{ width: "100%", minWidth: 0, mt: 1.75, display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.5, [LAPTOP_FIT_QUERY]: { mt: 0.75, gap: 1 } }}>
                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1, [LAPTOP_FIT_QUERY]: { gap: 0.5 } }}>
                    {stepLabel(1, "Provide product context to ensure code relevance and quality.")}
                    <Box
                        sx={{
                            ...cardSx,
                            p: 1.25,
                            flex: 1,
                            minHeight: 181,
                            display: "flex",
                            [LAPTOP_FIT_QUERY]: { minHeight: 212, maxHeight: 212, p: 1, overflow: "hidden" },
                            [STANDARD_LAPTOP_HEIGHT_QUERY]: { minHeight: 188, maxHeight: 188, p: 1, overflow: "hidden" }
                        }}
                    >
                        {contextMesh || (
                            <Typography variant="body2" color="text.secondary" sx={{ m: "auto" }}>
                                Context Mesh is unavailable.
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1, [LAPTOP_FIT_QUERY]: { gap: 0.5 } }}>
                    {stepLabel(2, "Define your product and its features and stories.")}
                    <FeatureStudioHierarchyCard onOpenHierarchy={onOpenHierarchy} />
                </Box>
            </Box>

            <Box sx={{ mt: 1.5, [LAPTOP_FIT_QUERY]: { mt: 0.75 } }}>
                {stepLabel(
                    3,
                    "Build your application by selecting Features and Stories then execute the Specification Driven Development (SDD) workflow. Create SDD work products defining specifications to build the application code.",
                    true
                )}
            </Box>
            <Box sx={{ width: "100%", minWidth: 0, mt: 1, display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.5, [LAPTOP_FIT_QUERY]: { mt: 0.5, gap: 1 } }}>
                <Box sx={{ ...cardSx, borderColor: "#103699", px: 1.75, py: 0.8, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".09em", textTransform: "uppercase", color: "#103699" }}>
                            Now building
                        </Typography>
                        <Typography noWrap title={`${selectedFeatureName} / ${selectedStoryName}`} sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>
                            {selectedFeatureName}{" "}
                            <Box component="span" sx={{ mx: 0.75, color: "#94a3b8" }}>
                                /
                            </Box>{" "}
                            {selectedStoryName}
                        </Typography>
                    </Box>
                    <Button
                        data-ui-bridge="sdd.open-scope-picker"
                        data-ui-label="Select Feature / Story"
                        data-ui-role="button"
                        data-ui-group="sdd-navigation"
                        data-ui-description="Open the Select Feature & Story page"
                        variant="outlined"
                        size="small"
                        startIcon={<GridViewOutlined sx={{ fontSize: "14px !important" }} />}
                        onClick={onOpenScopePicker}
                        sx={{ minWidth: 180, height: 34, textTransform: "none" }}
                    >
                        Select Feature / Story
                    </Button>
                </Box>
                <Box sx={{ ...cardSx, borderColor: "#103699", px: 1.25, py: 0.7, display: "flex", alignItems: "center", gap: 1 }}>
                    <ProgressDonut percent={totals.percent} size={36} />
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                        Overall SDD Progress on All Features and Stories{" "}
                        <Box component="span" sx={{ color: "#475569", fontWeight: 500 }}>
                            - {progressLoading ? "Loading…" : `${totals.accepted}/${totals.total} Work Products`}
                        </Box>
                    </Typography>
                    <Button
                        data-ui-bridge="sdd.open-work-product-progress-report"
                        data-ui-label="SDD Work Product Progress Report"
                        data-ui-role="button"
                        data-ui-group="sdd-navigation"
                        data-ui-description="Open the SDD Work Product Progress Report page"
                        disabled={!progress}
                        variant="outlined"
                        size="small"
                        onClick={onOpenProgressReport}
                        sx={{ height: 34, textTransform: "none" }}
                    >
                        View Details
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
